/**
 * Team views: agent rows and tiles (state by fill style plus a word, the current step and
 * the elapsed time of the whole run, never an invented total), handoffs, run logs, the
 * program ruler and the live feed.
 */

import { type ReactNode, useState } from 'react';
import type { Agent, AgentId, Handoff, LiveFeedItem, Run, RunStep, ScheduleItem } from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { Link } from '../app/router.js';
import { AGENT_NAMES, AGENT_SHAPES, accusative } from '../app/selectors.js';
import {
  formatClock,
  formatClockSeconds,
  formatDelta,
  formatDuration,
  formatPctValue,
  formatQuotaPct,
  formatSince,
  formatUntil,
  formatUsd,
  formatUsdPair,
  monthProgress,
  WEEKDAYS_SHORT,
  wallParts,
} from '../lib/format.js';
import { AgentGlyph, HandoffArrow, Icon, type IconName } from './icons.js';
import { Meter, StatusPill, StepTrail, TierBadge } from './primitives.js';

const AGENT_ORDER: AgentId[] = ['gm', 'temsilci', 'icerik', 'denetci'];
const SHORT_NAMES: Record<AgentId, string> = {
  gm: 'GM',
  temsilci: 'Temsilci',
  icerik: 'İçerik ve SEO',
  denetci: 'Denetçi',
};

function Since({ at }: { at: number }) {
  const text = formatSince(at);
  return (
    <span className="since">
      <span className="mono">{text.slice(0, 5)}</span>
      {text.slice(5)}
    </span>
  );
}

function elapsedSeconds(agent: Agent, now: number): number {
  return agent.progress ? Math.max(0, Math.round((now - agent.progress.startedAt) / 1000)) : 0;
}

function budgetRatio(agent: Agent): number | null {
  return agent.budget.kind === 'genesis' ? agent.budget.spent / agent.budget.cap : null;
}

/** Bugün micro row: glyph, name, pill and one line of activity. */
export function AgentMicroRow({ agent }: { agent: Agent }) {
  let activity: ReactNode;
  if (agent.state === 'working' && agent.progress) {
    activity = (
      <>
        {agent.shortTask} · <span className="mono">{agent.progress.doneSteps + 1}.</span> adım
      </>
    );
  } else if (agent.next) {
    activity = (
      <>
        sonraki <span className="mono">{formatClock(agent.next.at)}</span> {agent.next.label}
      </>
    );
  } else {
    activity = agent.shortTask;
  }
  return (
    <div className="micro">
      <AgentGlyph shape={agent.shape} />
      <span className="micro__name">{agent.name}</span>
      <span className="micro__pill">
        <StatusPill state={agent.state} />
      </span>
      <span className="micro__activity ellipsis">{activity}</span>
    </div>
  );
}

function PauseButton({ agent, size = 'row' }: { agent: Agent; size?: 'row' | 'tile' }) {
  const store = useStore();
  if (agent.state === 'paused') {
    return (
      <button type="button" className="text-btn agent-resume" onClick={() => void store.resumeAgent(agent.id)}>
        Devam et
      </button>
    );
  }
  const consequence =
    agent.state === 'working'
      ? 'mevcut adımı bitirip durur'
      : agent.next
        ? `${formatClock(agent.next.at)} çalışması bekletilir`
        : 'yeni iş başlatmaz';
  return (
    <button
      type="button"
      className={size === 'tile' ? 'icon-btn' : 'icon-hit'}
      aria-label={`${accusative(agent.name)} duraklat: ${consequence}`}
      onClick={() => void store.pauseAgent(agent.id)}
    >
      {size === 'tile' ? (
        <Icon name="pause" size={16} />
      ) : (
        <span className="icon-hit__box">
          <Icon name="pause" size={16} />
        </span>
      )}
    </button>
  );
}

function RetryButton({ agent }: { agent: Agent }) {
  const store = useStore();
  return (
    <button
      type="button"
      className="text-btn"
      onClick={() => store.showToast(`${agent.name} için yeniden deneme sunucu bağlanınca açılır`, { icon: 'info' })}
    >
      Yeniden dene
    </button>
  );
}

/** Phone agent row (spec 3.15). */
export function AgentRow({ agent, now }: { agent: Agent; now: number }) {
  const ratio = budgetRatio(agent);
  const tick = monthProgress(now);
  let third: ReactNode = null;
  if (agent.state === 'error' && agent.error) {
    third = (
      <span className="agent__actions">
        <RetryButton agent={agent} />
        <Link to="/kayitlar" className="text-btn">
          Kayda bak
        </Link>
      </span>
    );
  } else if (agent.progress && (agent.state === 'working' || agent.state === 'paused')) {
    third = (
      <StepTrail
        done={agent.progress.doneSteps}
        current={agent.progress.currentStep}
        elapsedSeconds={elapsedSeconds(agent, now)}
      />
    );
  } else if (agent.next) {
    third = (
      <span className="agent__line ellipsis">
        <span className="mono">{formatUntil(agent.next.at - now)}</span> sonra
        {agent.lastRunAt ? (
          <>
            {' · son çalışma '}
            <span className="mono">{formatClock(agent.lastRunAt)}</span>
          </>
        ) : null}
      </span>
    );
  } else if (agent.waitingNote) {
    third = (
      <span className="agent__line ellipsis">
        {agent.waitingNote}
        {agent.state === 'waiting' && agent.waitingRunId !== null ? (
          <>
            {' · '}
            <span className="mono">#{agent.waitingRunId}</span>
          </>
        ) : null}
      </span>
    );
  } else {
    third = (
      <span className="agent__line ellipsis">
        İş gelince başlar
        {agent.lastRunAt ? (
          <>
            {' · son çalışma '}
            <span className="mono">{formatClock(agent.lastRunAt)}</span>
          </>
        ) : null}
      </span>
    );
  }
  return (
    <li className="agent">
      <AgentGlyph shape={agent.shape} size={24} className="agent__glyph" />
      <div className="agent__head">
        <span className="agent__name">{agent.name}</span>
        <StatusPill state={agent.state} />
        {agent.stateSince && (agent.state === 'working' || agent.state === 'waiting') ? (
          <span className="agent__since">
            <Since at={agent.stateSince} />
          </span>
        ) : null}
      </div>
      <span className="agent__pause">
        <PauseButton agent={agent} />
      </span>
      <div className={agent.state === 'error' ? 'agent__task agent__task--error ellipsis' : 'agent__task ellipsis'}>
        {agent.state === 'error' && agent.error ? (
          <>
            {agent.error.message} · <span className="mono">{formatClock(agent.error.at)}</span> · {agent.error.attempts}{' '}
            deneme
          </>
        ) : agent.next && agent.state === 'idle' ? (
          <>
            Sonraki: <span className="mono">{formatClock(agent.next.at)}</span> {agent.next.label}
          </>
        ) : (
          agent.task
        )}
      </div>
      <div className="agent__third">{third}</div>
      <div className="agent__meta">
        <span className="ellipsis">
          {agent.todayLine}
          {' · '}
          <Link to={`/ekip/kisi/${agent.id}`} className="text-btn agent__history">
            Yaptıkları
          </Link>
        </span>
        <span className="mono agent__money">
          {agent.budget.kind === 'genesis'
            ? formatUsdPair(agent.budget.spent, agent.budget.cap)
            : `5 sa ${formatQuotaPct(agent.budget.window5hPct)}`}
        </span>
      </div>
      {ratio !== null ? (
        <Meter
          value={ratio}
          tick={tick}
          height={3}
          pace
          className="agent__meter"
          label={`${agent.name} bu ayki bütçesi yüzde ${Math.round(ratio * 100)} harcandı; ayın yüzde ${Math.round(tick * 100)}'ı geçti`}
        />
      ) : agent.budget.kind === 'claude_max' ? (
        <div className="agent__quota ellipsis">
          API karşılığı <span className="mono">{formatUsd(agent.budget.apiEquivalent)}</span> · faturalanmaz, Genesis'e
          eklenmez
        </div>
      ) : null}
    </li>
  );
}

/** Desktop agent tile (spec 3.15). */
export function AgentTile({ agent, now }: { agent: Agent; now: number }) {
  const ratio = budgetRatio(agent);
  const tick = monthProgress(now);
  const titleId = `tile-${agent.id}`;
  let line: ReactNode;
  if (agent.progress && (agent.state === 'working' || agent.state === 'paused')) {
    line = (
      <StepTrail
        done={agent.progress.doneSteps}
        current={agent.progress.currentStep}
        elapsedSeconds={elapsedSeconds(agent, now)}
        showElapsed={false}
      />
    );
  } else if (agent.next) {
    line = (
      <span className="tile__line">
        <Icon name="clock" size={12} stroke={2} />
        <span>
          <span className="mono">{formatUntil(agent.next.at - now)}</span> sonra başlar
        </span>
      </span>
    );
  } else {
    line = (
      <span className="tile__line">
        <HandoffArrow waiting />
        {agent.waitingNote ?? 'İş gelince başlar'}
      </span>
    );
  }
  return (
    <article className="tile" aria-labelledby={titleId}>
      <div className="tile__head">
        <AgentGlyph shape={agent.shape} size={24} />
        <div className="tile__names">
          <h3 id={titleId} className="tile__name ellipsis">
            {agent.name}
          </h3>
          <div className="tile__model mono">
            {agent.model}
            {agent.provider === 'Genesis' ? ' · Genesis' : ''}
          </div>
        </div>
        <PauseButton agent={agent} size="tile" />
      </div>
      <div className="tile__state">
        <StatusPill state={agent.state} />
        <span className="tile__since">
          {agent.state === 'working' && agent.progress ? (
            <>
              <span className="mono">#{agent.progress.runId}</span> · <Since at={agent.stateSince ?? now} />
            </>
          ) : agent.state === 'idle' && agent.lastRunAt ? (
            <>
              son çalışma <span className="mono">{formatClock(agent.lastRunAt)}</span>
            </>
          ) : agent.stateSince ? (
            <Since at={agent.stateSince} />
          ) : null}
        </span>
      </div>
      <p className="tile__task">{agent.desktopTask}</p>
      <div className="tile__trail">{line}</div>
      {agent.linkedOutput ? (
        <Link to={agent.linkedOutput.ref === 'gm-note' ? '/' : '/onaylar'} className="tile__link">
          {agent.linkedOutput.label}
          {agent.linkedOutput.ref.startsWith('#') ? (
            <>
              <Icon name="arrow-right" size={14} className="ink-2" />
              <span className="mono">{agent.linkedOutput.ref}</span>
            </>
          ) : (
            <Icon name="chevron-right" size={14} className="ink-3" />
          )}
        </Link>
      ) : agent.tileNote ? (
        <div className="tile__note">
          <Icon name="check" size={12} stroke={2.5} className="ok" />
          {agent.tileNote}
        </div>
      ) : (
        <div className="tile__note" />
      )}
      <div className="tile__foot">
        <span className="ellipsis ink-2">{agent.todayShort}</span>
        <span className="mono">
          {agent.budget.kind === 'genesis'
            ? `${formatUsdPair(agent.budget.spent, agent.budget.cap)} · ${formatPctValue(Math.round((ratio ?? 0) * 100))}`
            : `5 sa ${formatQuotaPct(agent.budget.window5hPct)}`}
        </span>
      </div>
      {ratio !== null ? (
        <Meter
          value={ratio}
          tick={tick}
          height={4}
          pace
          className="tile__meter"
          label={`${agent.name} bu ayki bütçesi yüzde ${Math.round(ratio * 100)} harcandı; ayın yüzde ${Math.round(tick * 100)}'ı geçti`}
        />
      ) : agent.budget.kind === 'claude_max' && agent.budget.window5hPct !== null ? (
        <Meter
          value={agent.budget.window5hPct / 100}
          height={4}
          className="tile__meter"
          label={`GM aboneliğinin 5 saatlik penceresinin yüzde ${agent.budget.window5hPct}'i kullanıldı`}
        />
      ) : null}
    </article>
  );
}

function HandoffState({ handoff }: { handoff: Handoff }) {
  if (handoff.state === 'waiting') {
    return (
      <span className="handoff__state">
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
          <circle cx="5" cy="5" r="4.1" fill="none" stroke="currentColor" strokeWidth="1.3" />
          <path d="M5 5V0.9A4.1 4.1 0 0 1 9.1 5Z" fill="currentColor" />
        </svg>
        Bekliyor
      </span>
    );
  }
  if (handoff.state === 'running') {
    return (
      <span className="handoff__state">
        <span className="handoff__dot" aria-hidden="true" />
        Sürüyor
      </span>
    );
  }
  if (handoff.state === 'failed') {
    return (
      <span className="handoff__state danger">
        <Icon name="x" size={12} stroke={2.5} />
        Olmadı {handoff.resolvedAt ? <span className="mono">{formatClock(handoff.resolvedAt)}</span> : null}
      </span>
    );
  }
  return (
    <span className="handoff__state ink-2">
      <Icon name="check" size={12} stroke={2.5} className="ok" />
      Geçti {handoff.resolvedAt ? <span className="mono">{formatClock(handoff.resolvedAt)}</span> : null}
    </span>
  );
}

function artifactIcon(kind: string): IconName {
  switch (kind) {
    case 'image':
      return 'image';
    case 'kb':
      return 'book-open';
    case 'product':
      return 'package';
    case 'queue':
      return 'square-check';
    default:
      return 'file-text';
  }
}

function ArtifactChip({ label, kind }: { label: string; kind: string }) {
  return (
    <span className="artifact">
      <Icon name={artifactIcon(kind)} size={12} />
      <span className={kind === 'kb' ? 'mono' : undefined}>{label}</span>
    </span>
  );
}

function stepDelta(steps: RunStep[], index: number): number | null {
  const previous = steps[index - 1];
  const step = steps[index];
  return previous && step ? Math.round((step.at - previous.at) / 1000) : null;
}

function StepText({ step, current }: { step: RunStep; current: boolean }) {
  return (
    <>
      {step.lead ? <strong>{step.lead} </strong> : null}
      {step.text}
      {step.verdict === 'passed' ? (
        <span className="nowrap">
          {' '}
          <Icon name="check" size={12} stroke={2.5} className="ok inline-icon" /> geçti
        </span>
      ) : null}
      {current ? <span className="sr-only"> (şu anki adım)</span> : null}
    </>
  );
}

/** Inline run log under an opened handoff (phone). */
export function RunLogInline({ run, now }: { run: Run; now: number }) {
  const running = run.status === 'running';
  const elapsed = Math.round(((run.finishedAt ?? now) - run.startedAt) / 1000);
  return (
    <div className="runlog-inline" id={`run-${run.id}`}>
      <ol aria-label={`Çalıştırma #${run.id} adımları${running ? ', sürüyor' : ''}`}>
        {run.steps.map((step, index) => {
          const current = running && index === run.steps.length - 1;
          const delta = stepDelta(run.steps, index);
          return (
            <li key={`${step.at}-${step.text}`} className="rl-step" aria-current={current ? 'step' : undefined}>
              {index < run.steps.length - 1 ? <span className="rl-step__line" aria-hidden="true" /> : null}
              <span className="rl-step__time">
                <span className="mono ink-2">{formatClockSeconds(step.at)}</span>
                {delta !== null ? <span className="mono ink-3 rl-step__delta">{formatDelta(delta)}</span> : null}
              </span>
              <span className="rl-step__node">
                {current ? <span className="node-live" /> : <span className="node" />}
              </span>
              <span className={current ? 'rl-step__text rl-step__text--current' : 'rl-step__text'}>
                <StepText step={step} current={current} />
                {step.artifact ? (
                  <>
                    {' '}
                    <ArtifactChip label={step.artifact.label} kind={step.artifact.kind} />
                  </>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="runlog-inline__foot">
        {running ? 'Sürüyor' : 'Bitti'} · <span className="mono">{formatDuration(elapsed)}</span> ·{' '}
        <span className="mono">{formatUsd(run.cost)}</span>
        {running ? ' şimdiye kadar' : ''}
      </p>
    </div>
  );
}

/** Phone handoff accordion; a row opens its run trace inline. */
export function HandoffList({
  handoffs,
  runs,
  now,
  focusRun = null,
}: {
  handoffs: Handoff[];
  runs: Run[];
  now: number;
  /** Opens the handoff of this run (deep link /ekip/:run), else the first one with a run log. */
  focusRun?: number | null;
}) {
  const withRun = (handoff: Handoff) => runs.some((run) => run.id === handoff.runId);
  const initial =
    handoffs.find((handoff) => focusRun !== null && handoff.runId === focusRun && withRun(handoff)) ??
    handoffs.find(withRun);
  const [open, setOpen] = useState<string | null>(initial?.id ?? null);
  return (
    <div className="handoffs">
      {handoffs.map((handoff) => {
        const run = runs.find((item) => item.id === handoff.runId) ?? null;
        const expanded = open === handoff.id;
        const showRunId = handoff.runId !== null && handoff.from !== 'gm';
        return (
          <div key={handoff.id} className={expanded ? 'handoff handoff--open' : 'handoff'}>
            <button
              type="button"
              className="handoff__row"
              aria-expanded={expanded}
              aria-controls={run ? `run-${run.id}` : undefined}
              onClick={() => setOpen(expanded ? null : handoff.id)}
            >
              <span className="handoff__line1">
                <span className="handoff__time mono">{formatClock(handoff.at)}</span>
                <AgentGlyph shape={AGENT_SHAPES[handoff.from]} />
                <span className="handoff__name">{SHORT_NAMES[handoff.from]}</span>
                <HandoffArrow waiting={handoff.state === 'waiting'} />
                <AgentGlyph shape={AGENT_SHAPES[handoff.to]} />
                <span className="handoff__name">{SHORT_NAMES[handoff.to]}</span>
                <HandoffState handoff={handoff} />
                <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={16} className="ink-3" />
              </span>
              <span className="handoff__line2">
                {handoff.subject}
                {handoff.note ? ` · ${handoff.note}` : null}
                {handoff.queuedTier ? (
                  <>
                    {' → Onay '}
                    <TierBadge tier={handoff.queuedTier} topic="DIŞ" variant="mini-tier" />
                  </>
                ) : null}
                {showRunId ? (
                  <>
                    {' · '}
                    <span className="mono">#{handoff.runId}</span>
                  </>
                ) : null}
              </span>
            </button>
            {expanded ? (
              run ? (
                <RunLogInline run={run} now={now} />
              ) : (
                <p className="runlog-inline runlog-inline__empty">Bu çalıştırmanın kaydı Kayıtlar'da.</p>
              )
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function laneLeft(index: number): string {
  return `calc(54px + (100% - 54px) * ${(index + 0.5) / 4})`;
}

/** Desktop handoff lanes: one column per agent, a line from the source to the target lane. */
export function HandoffLanes({
  handoffs,
  selected,
  onSelect,
}: {
  handoffs: Handoff[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="lanes">
      {[0, 1, 2, 3].map((lane) => (
        <span
          key={lane}
          className="lanes__sep"
          aria-hidden="true"
          style={{ left: `calc(54px + (100% - 54px) * ${lane / 4})` }}
        />
      ))}
      <div className="lanes__head">
        <span className="key ink-3">Saat</span>
        {AGENT_ORDER.map((id) => (
          <span key={id} className="lanes__agent">
            <AgentGlyph shape={AGENT_SHAPES[id]} />
            {AGENT_NAMES[id]}
          </span>
        ))}
      </div>
      {handoffs.map((handoff) => {
        const from = AGENT_ORDER.indexOf(handoff.from);
        const to = AGENT_ORDER.indexOf(handoff.to);
        const start = Math.min(from, to);
        const span = Math.abs(to - from);
        const isSelected = selected === handoff.id;
        const labelRight = from >= 2;
        const stateText =
          handoff.state === 'waiting'
            ? 'bekliyor'
            : handoff.state === 'running'
              ? 'sürüyor'
              : handoff.state === 'failed'
                ? 'olmadı'
                : 'geçti';
        return (
          <button
            key={handoff.id}
            type="button"
            className={isSelected ? 'lane-row lane-row--selected' : 'lane-row'}
            aria-pressed={isSelected}
            aria-label={`${formatClock(handoff.at)} ${AGENT_NAMES[handoff.from]} ${AGENT_NAMES[handoff.to]}'ye: ${handoff.subject}, ${stateText}${handoff.resolvedAt ? ` ${formatClock(handoff.resolvedAt)}` : ''}${handoff.queuedTier ? `, onayda ${handoff.queuedTier}` : ''}`}
            onClick={() => onSelect(handoff.id)}
          >
            <span className="lane-row__time mono">{formatClock(handoff.at)}</span>
            <span className="lane-row__dot" aria-hidden="true" style={{ left: `calc(${laneLeft(from)} - 3px)` }} />
            <span
              className={handoff.state === 'waiting' ? 'lane-row__line lane-row__line--dashed' : 'lane-row__line'}
              aria-hidden="true"
              style={{ left: laneLeft(start), width: `calc((100% - 54px) * ${span / 4} - 8px)` }}
            />
            <span className="lane-row__arrow" aria-hidden="true" style={{ left: `calc(${laneLeft(to)} - 8px)` }}>
              <svg width="7" height="8" viewBox="0 0 7 8" aria-hidden="true" focusable="false">
                <path d="M0 0L7 4L0 8Z" fill="currentColor" />
              </svg>
            </span>
            <span className="lane-row__end" aria-hidden="true" style={{ left: `calc(${laneLeft(to)} - 1px)` }}>
              {handoff.state === 'passed' ? (
                <svg width="14" height="14" viewBox="0 0 14 14" className="ok" aria-hidden="true" focusable="false">
                  <circle cx="7" cy="7" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M4.3 7.2 6.2 9 9.7 5.3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : handoff.state === 'running' ? (
                <span className="node-live" />
              ) : handoff.state === 'failed' ? (
                <Icon name="x" size={14} stroke={2.5} className="danger" />
              ) : (
                <span className="lane-row__open" />
              )}
            </span>
            <span className="lane-row__status" style={{ left: `calc(${laneLeft(to)} + 17px)` }}>
              {handoff.state === 'passed' && handoff.resolvedAt ? (
                <span className="mono">{formatClock(handoff.resolvedAt)}</span>
              ) : (
                stateText
              )}
            </span>
            <span
              className="lane-row__label"
              style={labelRight ? { right: 0 } : { left: `calc(${laneLeft(from)} - 3px)` }}
            >
              {handoff.subject}
              {handoff.note ? ` · ${handoff.note}` : null}
              {handoff.queuedTier ? (
                <>
                  <span className="ink-2"> → Onay</span>{' '}
                  <TierBadge tier={handoff.queuedTier} topic="DIŞ" variant="mini-tier" />
                </>
              ) : null}
              {handoff.runId !== null && handoff.from !== 'gm' && !labelRight ? (
                <>
                  <span className="ink-2"> · </span>
                  <span className="mono">#{handoff.runId}</span>
                </>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Desktop run log (spec 3.19) with deltas, the verifier step and artifact chips. */
export function RunLog({ run, now }: { run: Run; now: number }) {
  const running = run.status === 'running';
  return (
    <ol className="runlog" aria-label={`Çalıştırma ${run.id} adımları`}>
      <span className="runlog__rail" aria-hidden="true" />
      {run.steps.map((step, index) => {
        const delta = stepDelta(run.steps, index);
        const current = running && index === run.steps.length - 1;
        return (
          <li key={`${step.at}-${step.text}`} className="runlog__row">
            <span className="mono ink-2">{formatClockSeconds(step.at)}</span>
            <span
              className={
                delta !== null && delta >= 30 ? 'runlog__delta runlog__delta--slow mono' : 'runlog__delta mono'
              }
            >
              {delta !== null ? formatDelta(delta) : ''}
            </span>
            <span className="runlog__node">
              {current ? (
                <span className="node-live" />
              ) : step.node === 'verifier' ? (
                <AgentGlyph shape="hexagon" size={12} />
              ) : step.node === 'queue' ? (
                <TierBadge tier="T3" topic="DIŞ" variant="mini-tier" />
              ) : (
                <span className="node" />
              )}
            </span>
            <span className="runlog__text">
              <StepText step={step} current={current} />
            </span>
            {step.artifact ? <ArtifactChip label={step.artifact.label} kind={step.artifact.kind} /> : <span />}
          </li>
        );
      })}
      {running ? (
        <p className="runlog__foot">
          Sürüyor · <span className="mono">{formatDuration(Math.round((now - run.startedAt) / 1000))}</span>
        </p>
      ) : null}
    </ol>
  );
}

/** Where the day's events sit on the ruler: past and now on the left, the future to the right. */
function rulerPositions(
  items: ScheduleItem[],
  now: number,
): { items: { item: ScheduleItem; x: number }[]; nowX: number } {
  const past = items.filter((item) => item.at <= now);
  const future = items.filter((item) => item.at > now);
  const pastSlots = past.length + 1;
  const pastX = (index: number) => (pastSlots === 1 ? 0.425 : 0.073 + ((0.425 - 0.073) * index) / (pastSlots - 1));
  const futureX = (index: number) =>
    future.length === 1 ? 0.68 : 0.68 + ((0.888 - 0.68) * index) / Math.max(1, future.length - 1);
  return {
    items: [
      ...past.map((item, index) => ({ item, x: pastX(index) })),
      ...future.map((item, index) => ({ item, x: futureX(index) })),
    ],
    nowX: pastX(past.length),
  };
}

function scheduleTime(item: ScheduleItem, now: number): string {
  const a = wallParts(item.at);
  const b = wallParts(now);
  const sameDay = a.year === b.year && a.month === b.month && a.day === b.day;
  return sameDay ? formatClock(item.at) : `${WEEKDAYS_SHORT[a.weekday]} ${formatClock(item.at)}`;
}

/** Program as a dimension line: past solid, future dashed, a teal "now" marker. */
export function ProgramRuler({
  schedule,
  now,
  compact = false,
}: {
  schedule: ScheduleItem[];
  now: number;
  compact?: boolean;
}) {
  const { items, nowX } = rulerPositions(schedule, now);
  const description = [
    ...schedule.map(
      (item) => `${scheduleTime(item, now)} ${item.label.toLocaleLowerCase('tr-TR')}${item.done ? ' yapıldı' : ''}`,
    ),
    `şimdi ${formatClock(now)}`,
  ].join(', ');
  return (
    <div className={compact ? 'ruler ruler--compact' : 'ruler'} role="img" aria-label={`Program: ${description}`}>
      <svg
        className="ruler__arrow ruler__arrow--left"
        width="10"
        height="12"
        viewBox="0 0 10 12"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M7 2L2 6L7 10" />
      </svg>
      <span className="ruler__past" style={{ width: `calc(${nowX * 100}% - 2px)` }} />
      <span className="ruler__future" style={{ left: `${nowX * 100}%` }} />
      <svg
        className="ruler__arrow ruler__arrow--right"
        width="10"
        height="12"
        viewBox="0 0 10 12"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M3 2L8 6L3 10" />
      </svg>
      {items.map(({ item, x }) => (
        <span key={item.at} className="ruler__event" style={{ left: `${x * 100}%` }}>
          <span className={item.at <= now ? 'ruler__tick' : 'ruler__tick ruler__tick--future'} />
          {item.done && !compact ? <Icon name="check" size={10} stroke={3} className="ok ruler__check" /> : null}
          <span className="ruler__label">
            <span className="mono ruler__time">{scheduleTime(item, now)}</span>
            <span className="ruler__name">
              {item.shortLabel}
              {item.done && compact ? (
                <Icon name="check" size={9} stroke={3.5} className="ok ruler__inline-check" />
              ) : null}
            </span>
          </span>
        </span>
      ))}
      <span className="ruler__now" style={{ left: `${nowX * 100}%` }} />
      <span className="ruler__now-label key" style={{ left: `${nowX * 100}%` }}>
        Şimdi {formatClock(now)}
      </span>
    </div>
  );
}

export function LiveFeed({ items }: { items: LiveFeedItem[] }) {
  return (
    <ol className="feed">
      {items.slice(0, 4).map((item) => (
        <li key={item.id} className="feed__row">
          <span className="mono ink-2">{formatClockSeconds(item.at)}</span>
          <AgentGlyph shape={AGENT_SHAPES[item.agent]} />
          <span className="feed__text ellipsis">
            <strong>{AGENT_NAMES[item.agent]}</strong> {item.text}
          </span>
          <span className="mono ink-3">#{item.runId}</span>
        </li>
      ))}
    </ol>
  );
}

/** Genesis budget: dollars only; Claude Max quota is never added to it. */
export function GenesisLine() {
  const { snapshot, now } = usePanel();
  if (!snapshot) return null;
  const ratio = snapshot.genesis.spent / snapshot.genesis.cap;
  const tick = monthProgress(now);
  return (
    <Meter
      value={ratio}
      tick={tick}
      height={4}
      pace
      label={`Genesis bu ayki harcaması ${formatUsd(snapshot.genesis.spent)}, ${formatUsd(snapshot.genesis.cap, 'auto')} tavanın yüzde ${Math.round(ratio * 100)}'si; ayın yüzde ${Math.round(tick * 100)}'ı geçti`}
    />
  );
}
